import { useCallback, useMemo } from 'react';
import type { LocationPermissionResponse } from 'expo-location';

import {
  getLocationGrantType,
  type PermissionsMachineImplementations,
  useInitPermissionsMachine,
  usePermissionsMachine,
} from '@sg/shared-fe/features/permissions';

import type { FeatureFlagTypes } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { useLocationPermissionPrimerEnabled } from '../useLocationPermissionPrimerEnabled';
import { usePermissionsPrimerExperiment } from '../usePermissionsPrimerExperiment';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Drives the location permission primer flow for a specific entry point.
 * `variants` is the FF allow-list this consumer responds to.
 *
 * Returns the flags + handlers the consumer needs to decide whether to render
 * a loading screen, the primer, or its normal content.
 */
export const useLocationPermissionPrimer = (params: Params) => {
  const { variants } = params;

  const { track } = useTelemetry();
  const { canEnableLocationPrimer, locationPrimerVariant } = useLocationPermissionPrimerEnabled();
  const { trackExperimentStarted } = usePermissionsPrimerExperiment();

  // ─────────────────────────────────────────────────────────────────────

  const isLocationPrimerEnabled =
    canEnableLocationPrimer &&
    (locationPrimerVariant === 'disabled' ? false : variants.includes(locationPrimerVariant));

  // ─── Telemetry ───────────────────────────────────────────────────────

  const entryPoint = LOCATION_PRIMER_ENTRY_POINT[locationPrimerVariant];

  const telemetryPayload = useMemo(
    () => ({ entry_point: entryPoint, variant: locationPrimerVariant }),
    [entryPoint, locationPrimerVariant],
  );

  // ─── Actions ─────────────────────────────────────────────────────────

  const onLocationPrimerShow = useCallback(() => {
    trackExperimentStarted();
    track('location_primer_shown', telemetryPayload);
  }, [telemetryPayload, track, trackExperimentStarted]);

  const onRequestLocationPermission = useCallback(() => {
    track('location_primer_cta_tapped', telemetryPayload);
  }, [telemetryPayload, track]);

  const onSkipLocationPermission = useCallback(() => {
    track('location_primer_dismissed', {
      ...telemetryPayload,
      action: 'not_now',
    });
  }, [telemetryPayload, track]);

  const onLocationPermissionGranted = useCallback(
    (_args: unknown, permissionResponse: LocationPermissionResponse) => {
      track('location_permission_granted', {
        ...telemetryPayload,
        grant_type: getLocationGrantType(permissionResponse),
      });
    },
    [telemetryPayload, track],
  );

  const onLocationPermissionDenied = useCallback(
    (_args: unknown, _permissionResponse?: LocationPermissionResponse) => {
      track('location_permission_denied', telemetryPayload);
    },
    [telemetryPayload, track],
  );

  const onAllowOnceDetected = useCallback(() => {
    track('location_allow_once_detected', { variant: telemetryPayload.variant });
  }, [telemetryPayload, track]);

  // ─────────────────────────────────────────────────────────────────────

  const implementations = useMemo<PermissionsMachineImplementations>(
    () => ({
      actions: {
        onLocationPrimerShow,
        onRequestLocationPermission,
        onSkipLocationPermission,
        onLocationPermissionGranted,
        onLocationPermissionDenied,
        onAllowOnceDetected,
      },
    }),
    [
      onAllowOnceDetected,
      onLocationPermissionDenied,
      onLocationPermissionGranted,
      onLocationPrimerShow,
      onRequestLocationPermission,
      onSkipLocationPermission,
    ],
  );

  // ─── Actor ───────────────────────────────────────────────────────────

  const permissionsActorRef = useInitPermissionsMachine({
    isLocationPrimerEnabled,
    implementations,
  });

  const {
    isLocationPermissionsLoading,
    isLocationPrimerVisible,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    isLocationPermissionDenied,
    isLocationPermissionSkipped,
    isLocationPermissionAlreadyShown,
    requestLocationPermissions,
    skipLocationPermissions,
  } = usePermissionsMachine(permissionsActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPrimerActive =
    isLocationPrimerVisible || isRequestingLocationPermission || isSkippingLocationPermission;

  // ─────────────────────────────────────────────────────────────────────

  return {
    isLocationPrimerEnabled,
    isLocationPermissionsLoading,
    isPrimerActive,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    isLocationPermissionDenied,
    isLocationPermissionSkipped,
    isLocationPermissionAlreadyShown,
    onAllow: requestLocationPermissions,
    onSkip: skipLocationPermissions,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATION_PRIMER_ENTRY_POINT = {
  disabled: undefined,
  A: 'onboarding',
  B: 'location-search',
  C: 'location-search',
} as const satisfies Record<FeatureFlagTypes.LocationPrimerVariation, string | undefined>;

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  variants: Exclude<
    ReturnType<typeof useLocationPermissionPrimerEnabled>['locationPrimerVariant'],
    'disabled'
  >[];
};
